import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Podcast,
  ArrowLeft,
  ArrowRight,
  Check,
  Newspaper,
  Users,
  MessageCircle,
  Zap,
  Clock,
  BookOpen,
  Sparkles,
} from 'lucide-react';
import { useChatStore } from '../stores/chatStore';

// Style options
const STYLE_OPTIONS = [
  {
    id: 'news',
    name: 'News Talk',
    icon: Newspaper,
    description: 'Anchor and reporter dialogue format',
    color: 'blue',
  },
  {
    id: 'expert',
    name: 'Expert Interview',
    icon: Users,
    description: 'Host and expert in-depth analysis',
    color: 'purple',
  },
  {
    id: 'casual',
    name: 'Casual Talk',
    icon: MessageCircle,
    description: 'Friendly and relaxed conversation',
    color: 'pink',
  },
];

// Duration options
const DURATION_OPTIONS = [
  {
    id: 3,
    name: '3 min',
    icon: Zap,
    description: 'Quick highlights',
    color: 'emerald',
  },
  {
    id: 5,
    name: '5 min',
    icon: Clock,
    description: 'Balanced coverage',
    color: 'blue',
  },
  {
    id: 10,
    name: '10 min',
    icon: BookOpen,
    description: 'In-depth analysis',
    color: 'violet',
  },
];

const PodcastFunnel = ({ onGenerate, onCancel, hideCancel = false }) => {
  const {
    podcastFunnel,
    setPodcastTopic,
    setPodcastStyle,
    setPodcastDuration,
    goBackPodcastStep,
    resetPodcastFunnel,
  } = useChatStore();

  // onCancel prop이 있으면 사용, 없으면 store의 resetPodcastFunnel 사용
  const handleCancel = onCancel || resetPodcastFunnel;

  const { step, topic, style, duration } = podcastFunnel;
  const [inputValue, setInputValue] = useState(topic || '');

  // Step 1: 주제 입력
  const renderTopicStep = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="space-y-6"
    >
      <div className="text-center">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-purple-500/20">
          <Podcast className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-xl font-semibold text-text-100 mb-2">What topic should we cover?</h2>
        <p className="text-sm text-text-400">Paste an article or enter a topic</p>
      </div>

      <div className="space-y-3">
        <textarea
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder="e.g. Semiconductor market trends, Samsung earnings analysis..."
          className="w-full h-40 px-4 py-3 bg-bg-200/60 border border-bg-300/50 rounded-xl text-text-100 placeholder:text-text-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 resize-none"
        />
        <p className="text-xs text-text-500 text-right">{inputValue.length} chars</p>
      </div>

      <button
        onClick={() => {
          if (inputValue.trim()) {
            setPodcastTopic(inputValue.trim());
          }
        }}
        disabled={!inputValue.trim()}
        className={`w-full py-3 rounded-xl font-medium flex items-center justify-center gap-2 transition-all ${
          inputValue.trim()
            ? 'bg-purple-500 hover:bg-purple-400 text-white'
            : 'bg-bg-300 text-text-500 cursor-not-allowed'
        }`}
      >
        Next <ArrowRight className="w-4 h-4" />
      </button>
    </motion.div>
  );

  // Step 2: 스타일 선택
  const renderStyleStep = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="space-y-6"
    >
      <div className="text-center">
        <h2 className="text-xl font-semibold text-text-100 mb-2">What style do you prefer?</h2>
        <p className="text-sm text-text-400">Choose the podcast tone</p>
      </div>

      <div className="space-y-3">
        {STYLE_OPTIONS.map((option) => {
          const Icon = option.icon;
          const isSelected = style === option.id;
          return (
            <button
              key={option.id}
              onClick={() => setPodcastStyle(option.id)}
              className={`w-full p-4 rounded-xl border-2 transition-all flex items-center gap-4 ${
                isSelected
                  ? 'border-purple-500 bg-purple-500/10'
                  : 'border-bg-300/50 bg-bg-200/40 hover:border-purple-500/50 hover:bg-bg-200/60'
              }`}
            >
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                isSelected ? 'bg-purple-500 text-white' : 'bg-bg-300/50 text-text-300'
              }`}>
                <Icon className="w-6 h-6" />
              </div>
              <div className="flex-1 text-left">
                <p className={`font-medium ${isSelected ? 'text-purple-400' : 'text-text-200'}`}>
                  {option.name}
                </p>
                <p className="text-sm text-text-400">{option.description}</p>
              </div>
              {isSelected && (
                <Check className="w-5 h-5 text-purple-400" />
              )}
            </button>
          );
        })}
      </div>

      <div className="flex gap-3">
        <button
          onClick={goBackPodcastStep}
          className="flex-1 py-3 rounded-xl font-medium bg-bg-300/50 text-text-300 hover:bg-bg-300 transition-colors flex items-center justify-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
      </div>
    </motion.div>
  );

  // Step 3: Duration selection
  const renderDurationStep = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="space-y-6"
    >
      <div className="text-center">
        <h2 className="text-xl font-semibold text-text-100 mb-2">How long should it be?</h2>
        <p className="text-sm text-text-400">Choose the podcast length</p>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {DURATION_OPTIONS.map((option) => {
          const Icon = option.icon;
          const isSelected = duration === option.id;
          return (
            <button
              key={option.id}
              onClick={() => setPodcastDuration(option.id)}
              className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                isSelected
                  ? 'border-purple-500 bg-purple-500/10'
                  : 'border-bg-300/50 bg-bg-200/40 hover:border-purple-500/50'
              }`}
            >
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                isSelected ? 'bg-purple-500 text-white' : 'bg-bg-300/50 text-text-300'
              }`}>
                <Icon className="w-5 h-5" />
              </div>
              <p className={`font-semibold ${isSelected ? 'text-purple-400' : 'text-text-200'}`}>
                {option.name}
              </p>
              <p className="text-xs text-text-400">{option.description}</p>
            </button>
          );
        })}
      </div>

      <div className="flex gap-3">
        <button
          onClick={goBackPodcastStep}
          className="flex-1 py-3 rounded-xl font-medium bg-bg-300/50 text-text-300 hover:bg-bg-300 transition-colors flex items-center justify-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
      </div>
    </motion.div>
  );

  // Step 4: Final confirmation
  const renderConfirmStep = () => {
    const selectedStyle = STYLE_OPTIONS.find((s) => s.id === style);
    const selectedDuration = DURATION_OPTIONS.find((d) => d.id === duration);

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="space-y-6"
      >
        <div className="text-center">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-purple-500/20">
            <Sparkles className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-xl font-semibold text-text-100 mb-2">Ready to go!</h2>
          <p className="text-sm text-text-400">Generate the podcast with these settings?</p>
        </div>

        {/* Settings summary */}
        <div className="p-4 bg-bg-200/50 rounded-xl space-y-3">
          <div className="flex items-start gap-3">
            <span className="text-text-500 text-sm w-16">Topic</span>
            <span className="text-text-200 text-sm flex-1 line-clamp-2">{topic}</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-text-500 text-sm w-16">Style</span>
            <span className="text-text-200 text-sm flex items-center gap-2">
              {selectedStyle && <selectedStyle.icon className="w-4 h-4 text-purple-400" />}
              {selectedStyle?.name}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-text-500 text-sm w-16">Length</span>
            <span className="text-text-200 text-sm flex items-center gap-2">
              {selectedDuration && <selectedDuration.icon className="w-4 h-4 text-purple-400" />}
              {selectedDuration?.name}
            </span>
          </div>
        </div>

        <div className="flex gap-3">
          <button
            onClick={goBackPodcastStep}
            className="flex-1 py-3 rounded-xl font-medium bg-bg-300/50 text-text-300 hover:bg-bg-300 transition-colors flex items-center justify-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" /> Edit
          </button>
          <button
            onClick={() => {
              onGenerate({
                topic,
                style,
                duration,
              });
              resetPodcastFunnel();
            }}
            className="flex-1 py-3 rounded-xl font-medium bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-400 hover:to-pink-400 transition-all flex items-center justify-center gap-2 shadow-lg shadow-purple-500/20"
          >
            <Podcast className="w-4 h-4" /> Generate
          </button>
        </div>
      </motion.div>
    );
  };

  // 단계 인디케이터
  const StepIndicator = () => (
    <div className="flex items-center justify-center gap-2 mb-6">
      {[1, 2, 3, 4].map((s) => (
        <div
          key={s}
          className={`w-2 h-2 rounded-full transition-all ${
            s === step
              ? 'w-6 bg-purple-500'
              : s < step
              ? 'bg-purple-500/50'
              : 'bg-bg-300'
          }`}
        />
      ))}
    </div>
  );

  return (
    <div className="w-full max-w-md mx-auto py-8 px-4">
      <StepIndicator />

      <AnimatePresence mode="wait">
        {step === 1 && renderTopicStep()}
        {step === 2 && renderStyleStep()}
        {step === 3 && renderDurationStep()}
        {step === 4 && renderConfirmStep()}
      </AnimatePresence>

      {/* Cancel button - only shown when hideCancel is false */}
      {!hideCancel && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            console.log('Cancel button clicked');
            handleCancel();
          }}
          style={{ pointerEvents: 'auto' }}
          className="w-full mt-6 py-3 text-sm text-text-400 hover:text-text-200 hover:bg-bg-300/50 rounded-xl border border-bg-300/50 transition-all cursor-pointer relative z-50"
        >
          Cancel
        </button>
      )}
    </div>
  );
};

export default PodcastFunnel;
